<template>
	<div class="login-email-form">
		<h1 v-t="`onboarding_${field}_prompt`" />

		<FormItem
			:label="$t(`onboarding_loginmethod_${field}_button`)"
			class="concact-form"
			:error="!!contactError"
			:message="contactError"
		>
			<Input
				v-if="isEmail"
				class="concact-input"
				large
				v-model="contact"
				@enter="submit"
				autoFocus
				:type="isEmail ? 'email' : 'tel'"
				ref="input"
			/>
			<PhoneInput
				v-if="!isEmail"
				v-model="contact"
				:error="isMissing"
				large
				autoFocus
			/>
		</FormItem>

		<div
			class="tos"
			v-html="
				$t('onboarding_phone_termsofservice', [
					'https://www.pronto.io/user-terms',
					'https://www.pronto.io/privacy',
				])
			"
		/>

		<Button
			pronto
			large
			block
			@click="submit"
			:loading="isLoading"
			:disabled="!!contactError"
			class="send"
		>
			{{ $t('onboarding_send_code_button') }}
		</Button>

		<Button link pronto :to="switchTo" replace>
			{{ $t(`onboarding_phone_switchmode_${field}`) }}
		</Button>
	</div>
</template>

<script>
import { FormItem, Input, Button, PhoneInput } from '@/components/common'
import {
	verificationRoute,
	getUserData,
	setUserData,
	sendCodeToExistingUser,
	phoneRoute,
	emailRoute,
	clearUserData,
} from './index'

export default {
	name: 'EmailPhoneForm',
	components: { FormItem, Input, Button, PhoneInput },
	data() {
		const user = getUserData()
		const field = this.$route.name === phoneRoute.name ? 'phone' : 'email'
		return {
			contact: user?.[field] ?? null,
			isLoading: false,
			isMissing: false,
		}
	},
	watch: {
		contact() {
			this.isMissing = false
			this.errorCode = null
		},
	},
	computed: {
		isEmail() {
			return this.$route.name === 'signinemail'
		},
		contactError() {
			if (this.errorCode) {
				return this.$t(`apierrors_${this.errorCode}`)
			}
			if (this.isMissing) {
				return this.$t(`onboarding_missing_${this.field}_error`)
			}
			return null
		},
		field() {
			return this.isEmail ? 'email' : 'phone'
		},
		switchTo() {
			return {
				name: this.isEmail ? phoneRoute.name : emailRoute.name,
				params: this.$route.params,
			}
		},
	},
	methods: {
		async submit() {
			if (!this.contact) {
				this.isMissing = true
				this.$refs.input.focus()
				return
			}
			this.isMissing = false
			this.isLoading = true
			clearUserData()

			try {
				const body = { [this.field]: this.contact }
				const isOk = await this.sendCode(body)
				if (!isOk) {
					this.isLoading = false
					return
				}
				setUserData({
					[this.field]: this.contact,
					isExistingUser: true,
				})
				this.$router.push({
					name: verificationRoute.name,
					params: this.$route.params,
				})
			} catch (e) {
				console.error(e)
			}

			this.isLoading = false
		},

		async sendCode(body) {
			const { ok, error } = await sendCodeToExistingUser(body)
			if (!ok) {
				switch (error) {
					case 'PHONE_NOT_FOUND':
						this.$router.replace({
							name: emailRoute.name,
							params: this.$route.params,
						})
						this.$alert.error(this.$t('apierrors_PHONE_NOT_FOUND'))
						break
					case 'EMAIL_NOT_FOUND':
						this.$router.replace({
							name: phoneRoute.name,
							params: this.$route.params,
						})
						this.$alert.error(this.$t('apierrors_EMAIL_NOT_FOUND'))
						break
					case 'SEND_LIMIT_REACHED':
					case 'INVALID_PHONE_PHONE':
					case 'INVALID_EMAIL_EMAIL':
						this.errorCode = error
						break
					default:
						this.$alert.error(this.$t('apierrors_error_generic'))
						break
				}
			}
			return ok
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.login-email-form {
	@include flex-column-center;

	> h1 {
		margin-bottom: 70px;
	}

	.concact-form {
		align-self: stretch;
		margin-bottom: 16px;
	}

	.tos {
		align-self: stretch;
		margin-bottom: 110px;
		font-size: 12px;
		color: var(--neutral-400);
		:deep(a) {
			color: var(--teal-300);
			text-decoration: none;
		}
	}

	.send {
		display: flex;
		margin-bottom: 16px;
	}
}
</style>
